import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Button, Input, Modal, message } from "antd";
import { createPublicOfferLinkRequest } from "../../../services/publicOfferLinksApi";
import { getUserInfo } from "../../../services/localStorage";
import {
  ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE,
  buildAgentBrandingFromUser,
  buildOrganizedTourQuoteSnapshotPayload,
  computeOrganizedTourUiQuotePricing,
} from "../utils/organizedTourQuoteSnapshotPayload";
import { downloadOrganizedTourProfileOfferPdf } from "../utils/downloadOrganizedTourLdsOfferPdf";
import { tokenToPublicOfferUrl } from "../utils/organizedTourPublicQuoteUrl";
import { get_depret_from_result, parseToLdsDate } from "../../../utils/LDSHelpers";
import { useOrganizedTourDisplayedQuotePricing } from "../hooks/useOrganizedTourDisplayedQuotePricing";

export default function OrganizedTourSmartQuoteActions({
  selectedResult,
  ourPrice,
  orgTourQuoteOverride,
  itineraryQuoteSourceText,
  dealId,
  ldsOrderId,
  roomsAndPaxInfo = null,
  triggerClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [busyLink, setBusyLink] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  /** מוצג ב־UI רק לאחר לחיצה מפורשת על «צור קישור» */
  const [linkVisibleInUi, setLinkVisibleInUi] = useState(false);
  const [offerLinkUrl, setOfferLinkUrl] = useState("");
  const offerTokenRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBusyLink(false);
    setBusyPdf(false);
    setLinkVisibleInUi(false);
    setOfferLinkUrl("");
    offerTokenRef.current = null;
  }, [open]);

  const quotePricing = useOrganizedTourDisplayedQuotePricing({
    selectedResult,
    ourPrice,
    orgTourQuoteOverride,
  });

  const quote = useMemo(
    () => computeOrganizedTourUiQuotePricing(quotePricing),
    [quotePricing]
  );

  const persistPayload = useCallback(() => {
    if (!customerName.trim() || !customerPhone.trim()) {
      message.warning("יש להזין לפחות שם וטלפון לקוח להצעה");
      throw new Error("missing_customer_contact");
    }

    const user = getUserInfo() || {};
    const branding = buildAgentBrandingFromUser(user);

    return buildOrganizedTourQuoteSnapshotPayload({
      ...quotePricing,
      itinerarySourceText: itineraryQuoteSourceText || "",
      termsSourceText: ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      customerEmail: customerEmail.trim() || null,
      branding,
      dealId,
      roomsAndPaxInfo,
    }).payload;
  }, [
    itineraryQuoteSourceText,
    quotePricing,
    customerEmail,
    customerName,
    customerPhone,
    dealId,
    roomsAndPaxInfo,
  ]);

  const assertSessionOrToast = useCallback(() => {
    const user = getUserInfo() || {};
    const sid = user.sessionID || user.sessionId;
    if (!sid) {
      message.error("יש להתחבר מחדש כדי ליצור קישור");
      return null;
    }
    return { user, sid };
  }, []);

  const createLinkOnServer = useCallback(
    async (payloadBuilt, opts) => {
      const sess = assertSessionOrToast();
      if (!sess) throw new Error("no_session");

      const res = await createPublicOfferLinkRequest({
        sessionID: sess.sid,
        agent_id: sess.user.id ?? sess.user.agentId ?? null,
        payload: payloadBuilt,
        final_price_ils: quote.finalQuotePrice,
        ttl_hours: 48,
      });

      if (!res?.success || !res?.token) {
        throw new Error(res?.error?.message || "יצירת הקישור נכשלה");
      }

      const url = tokenToPublicOfferUrl(res.token);
      offerTokenRef.current = String(res.token);
      setOfferLinkUrl(url);

      const mode = opts?.mode || "explicit_ui";
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_SMART_QUOTE_LINK_CREATE]", {
        mode,
        offer_type: "organized_tour",
        hasToken: true,
      });

      return String(res.token);
    },
    [assertSessionOrToast, quote.finalQuotePrice]
  );

  const handleCreateLinkOnly = useCallback(async () => {
    setBusyLink(true);
    let payloadBuilt;
    try {
      payloadBuilt = persistPayload();
    } catch {
      setBusyLink(false);
      return;
    }

    try {
      if (offerTokenRef.current) {
        const url = tokenToPublicOfferUrl(offerTokenRef.current);
        setOfferLinkUrl(url);
        setLinkVisibleInUi(true);
        // eslint-disable-next-line no-console
        console.log("[ORG_TOUR_SMART_QUOTE_EXISTING_LINK_REUSED]", {
          offer_type: "organized_tour",
          phase: "create_link_clicked",
        });
        message.success("הקישור כבר קיים — מוצג ללא יצירה מחודשת");
        return;
      }
      await createLinkOnServer(payloadBuilt, { mode: "explicit_ui" });
      setLinkVisibleInUi(true);
      message.success("נוצר קישור ציבורי להצעה");
    } catch (err) {
      message.error(
        err?.message || err?.response?.data?.error?.message || "יצירת הקישור נכשלה"
      );
    } finally {
      setBusyLink(false);
    }
  }, [persistPayload, createLinkOnServer]);

  const handleProducePdfOnly = useCallback(async () => {
    setBusyPdf(true);
    let payloadBuilt;
    try {
      payloadBuilt = persistPayload();
    } catch {
      setBusyPdf(false);
      return;
    }

    try {
      const sess = assertSessionOrToast();
      if (!sess) {
        return;
      }

      const { depDate, retDate } = get_depret_from_result(selectedResult) || {};
      const quotePdfPostBody = {
        sessionID: sess.sid,
        agent_id: sess.user?.id ?? sess.user?.agentId ?? null,
        ...payloadBuilt,
        supplier_id: selectedResult?.supplierDetails?.supplierId,
        dep_date: depDate != null ? parseToLdsDate(depDate) : "",
        ret_date: retDate != null ? parseToLdsDate(retDate) : "",
        price_ils: quote.finalQuotePrice,
      };

      await downloadOrganizedTourProfileOfferPdf({
        ldsOrderId: ldsOrderId || null,
        totalPriceIls: quote.finalQuotePrice,
        quotePdfPostBody: ldsOrderId ? null : quotePdfPostBody,
      });

      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_SMART_QUOTE_PDF_OPEN]", {
        offer_type: "organized_tour",
        fromExistingOrder: Boolean(ldsOrderId),
      });

      message.success("הקובץ ירד — פתחו אותו מההורדות");
    } catch (err) {
      message.error(err?.message || err?.response?.data?.error?.message || "הפקת PDF נכשלה");
    } finally {
      setBusyPdf(false);
    }
  }, [
    persistPayload,
    assertSessionOrToast,
    quote.finalQuotePrice,
    ldsOrderId,
    selectedResult,
  ]);

  const bothBusy = busyLink || busyPdf;

  return (
    <>
      <button
        type="button"
        className={classNames("otp-agent-panel__btn", "otp-agent-panel__btn--ghost", triggerClassName)}
        onClick={() => setOpen(true)}
      >
        הצעת מחיר חכמה
      </button>
      <Modal
        destroyOnHidden
        title="הצעת מחיר חכמה לטיול מאורגן"
        cancelText="סגור"
        onCancel={() => setOpen(false)}
        open={open}
        width={820}
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)} disabled={bothBusy}>
            סגור
          </Button>,
          <Button
            key="pdf"
            loading={busyPdf}
            disabled={busyLink}
            onClick={() => handleProducePdfOnly()}
          >
            הפק PDF
          </Button>,
          <Button
            key="link"
            type="primary"
            loading={busyLink}
            disabled={busyPdf}
            onClick={() => handleCreateLinkOnly()}
          >
            צור קישור
          </Button>,
        ]}
      >
        <div dir="rtl" style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, color: "#555" }}>
            «צור קישור» יוצר קישור ציבורי ללקוח. «הפק PDF» מוריד קובץ PDF מהשרת (לאחר שמירת הזמנה ב-LDS); אם טרם
            נוצר קישור, הנתונים נשמרים בשרת פעם אחת ברקע.
          </p>
          <label>
            שם לקוח*
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={bothBusy}
            />
          </label>
          <label>
            טלפון לקוח*
            <Input
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              disabled={bothBusy}
            />
          </label>
          <label>
            מייל (אופציונלי)
            <Input
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              disabled={bothBusy}
            />
          </label>
          <p style={{ margin: 0 }}>
            מחיר שיוצג ללקוח:{" "}
            <strong>
              {Number.isFinite(quote.finalQuotePrice)
                ? `${Math.round(quote.finalQuotePrice).toLocaleString("he-IL")} ₪`
                : "—"}
            </strong>
          </p>
          <p style={{ margin: "-4px 0 0", fontSize: 12, color: "#888", lineHeight: 1.45 }}>
            מחיר זה יישמר בעת יצירת הצעה / PDF מתוך כאן עם פרטי הלקוח שמילאת.
          </p>
          {linkVisibleInUi && offerLinkUrl ? (
            <div style={{ padding: 8, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
              הקישור:{" "}
              <a href={offerLinkUrl} target="_blank" rel="noreferrer">
                {offerLinkUrl}
              </a>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
